import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Save, CheckCircle2, Trash2, Loader2, AlertTriangle, Users } from "lucide-react";
import { toast } from "sonner";
import { FoodSelect, PageHead, apiErr, fmtQ, newReqId, toNum, todayISO, useMerendaMeta } from "@/merenda/common";
import { ConfirmedList } from "@/merenda/ConfirmedList";

const blank = () => ({ food_id: "", qty: "", note: "", additional: false, substitution: true });

export default function ConsumoDia() {
  const { meta, foods, scopeParams } = useMerendaMeta();
  const [date, setDate] = useState(todayISO());
  const [group, setGroup] = useState("MANHA");
  const [meal, setMeal] = useState("ALMOCO");
  const [day, setDay] = useState(null);
  const [items, setItems] = useState([]);
  const [noPrep, setNoPrep] = useState(false);
  const [just, setJust] = useState("");
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [stockIssues, setStockIssues] = useState([]);
  const reqId = useRef(newReqId());
  const foodMap = Object.fromEntries(foods.map((f) => [f.id, f]));

  const load = async () => {
    const { data } = await api.get("/merenda/day", { params: { date, group, meal, ...scopeParams } });
    setDay(data); setConflicts([]); setStockIssues([]);
    if (data.draft) {
      setItems(data.draft.items.map((i) => ({ ...i, qty: String(i.qty) })));
      setNoPrep(!!data.draft.no_prep); setJust(data.draft.justification || "");
    } else {
      setItems(data.menu_foods.map((m) => ({ ...blank(), food_id: m.food_id, substitution: false })));
      setNoPrep(false); setJust("");
    }
  };
  useEffect(() => { if (meta) load().catch((e) => toast.error(apiErr(e))); }, [meta, date, group, meal]); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (i, k, v) => setItems((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const payload = (action) => ({
    date, group, meal, action, no_prep: noPrep, justification: just, request_id: reqId.current,
    items: noPrep ? [] : items.filter((i) => i.food_id && toNum(i.qty) > 0).map((i) => ({ ...i, qty: toNum(i.qty) })),
  });

  const submit = async (action) => {
    const p = payload(action);
    if (!noPrep && items.some((i) => i.food_id && !(toNum(i.qty) > 0))) return toast.error("Informe a quantidade utilizada de cada alimento (ou remova a linha)");
    if (action === "confirm" && !noPrep && !p.items.length) return toast.error("Adicione ao menos um alimento com quantidade");
    setBusy(true);
    try {
      await api.post("/merenda/consumptions", p, { params: scopeParams });
      toast.success(action === "draft" ? "Rascunho salvo (não baixa estoque)" : "Consumo confirmado e baixado do estoque");
      reqId.current = newReqId();
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.conflicts) setConflicts(d.conflicts);
      if (d?.stock_issues) setStockIssues(d.stock_issues);
      reqId.current = newReqId();
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  if (!meta) return <div className="grid min-h-[40vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>;
  const locked = day?.period?.status === "APROVADO";
  const usedConfirmed = new Set((day?.confirmed || []).flatMap((d) => d.items.map((i) => i.food_id)));

  return (
    <div className="mx-auto max-w-3xl" data-testid="consumo-dia-page">
      <PageHead title="Consumo do Dia" subtitle="Informe o que foi realmente utilizado. Sugestões nunca viram consumo automaticamente." />
      <Card className="mb-4 grid grid-cols-1 gap-3 border-slate-200 p-4 sm:grid-cols-3">
        <div><Label>Data</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="consumo-date" /></div>
        <div><Label>Turno / grupo</Label>
          <select className="h-10 w-full rounded-md border border-slate-200 px-2 text-sm" value={group} onChange={(e) => setGroup(e.target.value)} data-testid="consumo-group">
            {meta.groups.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></div>
        <div><Label>Refeição</Label>
          <select className="h-10 w-full rounded-md border border-slate-200 px-2 text-sm" value={meal} onChange={(e) => setMeal(e.target.value)} data-testid="consumo-meal">
            {meta.meals.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></div>
      </Card>

      {day && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm" data-testid="consumo-day-info">
          <Badge className="bg-blue-50 text-blue-700"><Users className="mr-1 h-3.5 w-3.5" /> {day.presence_status === "PENDENTE" ? "Presença pendente de conferência" : `${day.comensais} aluno(s) presentes nesta refeição`}</Badge>
          {!day.planned && <Badge className="bg-amber-100 text-amber-800">Refeição não prevista para este turno</Badge>}
          {day.menu && <span className="text-slate-500">Cardápio: <b>{day.menu.preparation}</b></span>}
          {day.draft && <Badge className="bg-slate-200 text-slate-700">Rascunho carregado</Badge>}
          {locked && <Badge className="bg-red-100 text-red-700">Mês aprovado — bloqueado</Badge>}
        </div>
      )}

      {stockIssues.length > 0 && (
        <Card className="mb-4 border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="consumo-stock-issues">
          <div className="mb-1 flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> Estoque insuficiente nesta data — avise a direção</div>
          {stockIssues.map((s) => <div key={s.food_id}>{s.food}: disponível {fmtQ(s.available_on_date)} {s.unit}, solicitado {fmtQ(s.requested)} {s.unit}</div>)}
        </Card>
      )}

      <Card className="mb-4 border-slate-200 p-4">
        <div className="mb-3 flex items-center justify-between">
          <Label className="flex items-center gap-2"><Switch checked={noPrep} onCheckedChange={setNoPrep} data-testid="consumo-noprep-switch" /> Sem preparo / sem consumo</Label>
        </div>
        {noPrep ? (
          <Textarea placeholder="Justificativa (obrigatória)" value={just} onChange={(e) => setJust(e.target.value)} data-testid="consumo-justification" />
        ) : (
          <div className="space-y-3">
            {items.map((it, i) => {
              const f = foodMap[it.food_id];
              const sug = it.food_id ? day?.suggestions?.[it.food_id] : null;
              const dup = usedConfirmed.has(it.food_id) || conflicts.includes(f?.name);
              return (
                <div key={i} className={`rounded-xl border p-3 ${dup ? "border-amber-300 bg-amber-50" : "border-slate-200"}`} data-testid={`consumo-item-${i}`}>
                  <div className="flex gap-2">
                    <FoodSelect foods={foods} value={it.food_id} onChange={(v) => upd(i, "food_id", v)} testid={`consumo-food-${i}`} />
                    <Button variant="ghost" size="icon" className="text-rose-600" onClick={() => setItems((r) => r.filter((_, j) => j !== i))} data-testid={`consumo-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Quantidade utilizada</Label>
                      <div className="flex items-center gap-1">
                        <Input inputMode="decimal" placeholder="ex.: 2,5" value={it.qty} onChange={(e) => upd(i, "qty", e.target.value)} data-testid={`consumo-qty-${i}`} />
                        <span className="w-8 text-sm font-semibold text-slate-500" data-testid={`consumo-unit-${i}`}>{f?.unit || ""}</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Sugestão (não preenche)</Label>
                      <div className="flex h-10 items-center rounded-md bg-slate-50 px-2 text-sm text-slate-500" data-testid={`consumo-suggestion-${i}`} title={sug?.basis}>
                        {sug ? `${fmtQ(sug.qty)} ${f?.unit} (${fmtQ(sug.ref_per_service)} × ${sug.comensais})` : "Sem histórico/referência"}
                      </div>
                      {sug && <p className="mt-0.5 text-[11px] leading-tight text-slate-400" data-testid={`consumo-suggestion-basis-${i}`}>{sug.basis}</p>}
                      {sug && toNum(it.qty) > 0 && <p className="text-[11px] font-medium text-slate-500" data-testid={`consumo-diff-${i}`}>Diferença: {fmtQ(toNum(it.qty) - sug.qty)} {f?.unit}</p>}
                    </div>
                  </div>
                  <Input className="mt-2" placeholder="Observação (opcional)" value={it.note || ""} onChange={(e) => upd(i, "note", e.target.value)} data-testid={`consumo-note-${i}`} />
                  {dup && (
                    <label className="mt-2 flex items-center gap-2 text-xs font-medium text-amber-800">
                      <input type="checkbox" checked={it.additional} onChange={(e) => upd(i, "additional", e.target.checked)} data-testid={`consumo-additional-${i}`} />
                      Já existe lançamento confirmado deste alimento. Marque para lançar como consumo adicional.
                    </label>
                  )}
                </div>
              );
            })}
            <Button variant="outline" onClick={() => setItems((r) => [...r, blank()])} data-testid="consumo-add-food"><Plus className="mr-1 h-4 w-4" /> Adicionar alimento</Button>
          </div>
        )}
      </Card>

      <div className="sticky bottom-2 z-10 grid grid-cols-2 gap-2 rounded-xl bg-white/90 p-2 shadow-lg backdrop-blur">
        <Button variant="outline" disabled={busy || locked} onClick={() => submit("draft")} data-testid="consumo-save-draft"><Save className="mr-1 h-4 w-4" /> Salvar rascunho</Button>
        <Button className="bg-orange-500 hover:bg-orange-600" disabled={busy || locked} onClick={() => submit("confirm")} data-testid="consumo-confirm">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="mr-1 h-4 w-4" /> Confirmar consumo</>}
        </Button>
      </div>

      <ConfirmedList docs={day?.confirmed || []} foodMap={foodMap} locked={locked} scopeParams={scopeParams} onChange={load} />
    </div>
  );
}
